import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, RefreshControl } from 'react-native';
import {
  Card,
  Text,
  Button,
  Chip,
  Surface,
  useTheme,
  ActivityIndicator,
  ProgressBar,
  Avatar,
  List,
  Divider,
} from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import {
  Brain,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  Zap,
  BookOpen,
  BarChart3,
  Settings,
  Play,
  TestTube,
  Lightbulb,
  Search,
  GraduationCap,
  ChartLine,
  MessageCircle,
  UserCheck,
  BookOpenCheck,
  Microscope,
  Cpu,
  Shield,
  Star,
} from 'lucide-react-native';

interface AIFeature {
  id: string;
  title: string;
  description: string;
  status: 'active' | 'beta' | 'experimental' | 'planned';
  complexity: 'low' | 'medium' | 'high';
  userCount: number;
  successRate: number;
  lastUpdated: string;
  icon: React.ReactNode;
  route: string;
}

export function AITestingHubScreen() {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();
  const [refreshing, setRefreshing] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<string>('all');

  // Mock AI features data - in real app, this would come from backend
  const aiFeatures: AIFeature[] = [
    {
      id: 'smart-onboarding',
      title: 'Smart Onboarding',
      description:
        'AI-powered personalized onboarding flow that adapts to user preferences and learning style',
      status: 'active',
      complexity: 'medium',
      userCount: 1247,
      successRate: 94.2,
      lastUpdated: '2024-01-15',
      icon: <Brain size={24} color={theme.colors.primary} />,
      route: 'AIOnboarding',
    },
    {
      id: 'journey-flow',
      title: 'AI Journey Flow',
      description:
        'Dynamic journey creation and optimization based on user progress and preferences',
      status: 'active',
      complexity: 'high',
      userCount: 892,
      successRate: 87.6,
      lastUpdated: '2024-01-12',
      icon: <Target size={24} color={theme.colors.secondary} />,
      route: 'AIJourneyFlow',
    },
    {
      id: 'adaptive-learning',
      title: 'Adaptive Learning',
      description:
        'Real-time difficulty adjustment and content sequencing optimization',
      status: 'beta',
      complexity: 'high',
      userCount: 456,
      successRate: 91.8,
      lastUpdated: '2024-01-10',
      icon: <TrendingUp size={24} color={theme.colors.tertiary} />,
      route: 'AIAdaptiveLearning',
    },
    {
      id: 'personalization',
      title: 'AI Personalization',
      description:
        'Content and experience personalization based on user behavior and preferences',
      status: 'active',
      complexity: 'medium',
      userCount: 2034,
      successRate: 89.3,
      lastUpdated: '2024-01-14',
      icon: <Users size={24} color={theme.colors.primary} />,
      route: 'AIPersonalization',
    },
    {
      id: 'analytics',
      title: 'AI Analytics',
      description:
        'Advanced analytics and insights powered by machine learning',
      status: 'beta',
      complexity: 'medium',
      userCount: 678,
      successRate: 85.7,
      lastUpdated: '2024-01-08',
      icon: <BarChart3 size={24} color={theme.colors.secondary} />,
      route: 'AIAnalytics',
    },
    {
      id: 'intelligent-search',
      title: 'Intelligent Search',
      description:
        'AI-powered search with personalized recommendations and smart filtering',
      status: 'active',
      complexity: 'high',
      userCount: 1856,
      successRate: 92.1,
      lastUpdated: '2024-01-16',
      icon: <Search size={24} color={theme.colors.primary} />,
      route: 'IntelligentSearch',
    },
    {
      id: 'ai-assessment',
      title: 'AI Skill Assessment',
      description:
        'Interactive coffee skill assessment with personalized learning recommendations',
      status: 'active',
      complexity: 'medium',
      userCount: 1123,
      successRate: 88.5,
      lastUpdated: '2024-01-13',
      icon: <BookOpenCheck size={24} color={theme.colors.secondary} />,
      route: 'AIAssessment',
    },
    {
      id: 'ai-tutoring',
      title: 'AI Tutoring System',
      description:
        'Personalized AI tutoring with adaptive explanations and practice recommendations',
      status: 'beta',
      complexity: 'high',
      userCount: 567,
      successRate: 90.3,
      lastUpdated: '2024-01-11',
      icon: <GraduationCap size={24} color={theme.colors.tertiary} />,
      route: 'AITutoringDashboard',
    },
    {
      id: 'premium-analytics',
      title: 'Premium Analytics',
      description:
        'Advanced analytics dashboard with predictive modeling and coaching insights',
      status: 'active',
      complexity: 'high',
      userCount: 234,
      successRate: 94.7,
      lastUpdated: '2024-01-17',
      icon: <ChartLine size={24} color={theme.colors.primary} />,
      route: 'PremiumAnalyticsDashboard',
    },
    {
      id: 'social-learning',
      title: 'Social Learning Analytics',
      description:
        'AI-powered social learning features with peer matching and collaborative insights',
      status: 'beta',
      complexity: 'medium',
      userCount: 445,
      successRate: 86.2,
      lastUpdated: '2024-01-09',
      icon: <MessageCircle size={24} color={theme.colors.secondary} />,
      route: 'SocialLearningAnalytics',
    },
    {
      id: 'peer-matching',
      title: 'AI Peer Matching',
      description:
        'Intelligent peer matching system for collaborative learning experiences',
      status: 'experimental',
      complexity: 'high',
      userCount: 89,
      successRate: 78.9,
      lastUpdated: '2024-01-05',
      icon: <UserCheck size={24} color={theme.colors.tertiary} />,
      route: 'PeerMatching',
    },
    {
      id: 'adaptive-progress',
      title: 'Adaptive Progress',
      description:
        'AI-powered progress tracking with real-time adjustments and celebrations',
      status: 'active',
      complexity: 'medium',
      userCount: 1456,
      successRate: 91.4,
      lastUpdated: '2024-01-14',
      icon: <Cpu size={24} color={theme.colors.primary} />,
      route: 'AdaptiveProgress',
    },
    {
      id: 'progress-insights',
      title: 'Progress Insights',
      description:
        'Advanced progress tracking with AI-generated insights and recommendations',
      status: 'beta',
      complexity: 'medium',
      userCount: 678,
      successRate: 87.8,
      lastUpdated: '2024-01-12',
      icon: <Microscope size={24} color={theme.colors.secondary} />,
      route: 'ProgressInsights',
    },
    {
      id: 'notification-center',
      title: 'Notification Center',
      description:
        'AI-powered notification system with optimal timing and personalization',
      status: 'experimental',
      complexity: 'high',
      userCount: 123,
      successRate: 82.1,
      lastUpdated: '2024-01-07',
      icon: <Shield size={24} color={theme.colors.tertiary} />,
      route: 'NotificationCenter',
    },
    {
      id: 'mastery-visualization',
      title: 'Mastery Visualization',
      description:
        'Interactive skill mastery visualization with AI-driven progress tracking',
      status: 'active',
      complexity: 'medium',
      userCount: 789,
      successRate: 89.6,
      lastUpdated: '2024-01-13',
      icon: <Star size={24} color={theme.colors.primary} />,
      route: 'MasteryVisualization',
    },
  ];

  const onRefresh = async () => {
    setRefreshing(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setRefreshing(false);
  };

  const handleFeaturePress = (feature: AIFeature) => {
    (navigation as any).navigate(feature.route);
  };

  const getStatusColor = (status: AIFeature['status']) => {
    switch (status) {
      case 'active':
        return theme.colors.primary;
      case 'beta':
        return theme.colors.secondary;
      case 'experimental':
        return theme.colors.tertiary;
      case 'planned':
        return theme.colors.outline;
      default:
        return theme.colors.outline;
    }
  };

  const getComplexityColor = (complexity: AIFeature['complexity']) => {
    switch (complexity) {
      case 'low':
        return theme.colors.primary;
      case 'medium':
        return theme.colors.secondary;
      case 'high':
        return theme.colors.tertiary;
      default:
        return theme.colors.outline;
    }
  };

  const filteredFeatures =
    selectedFilter === 'all'
      ? aiFeatures
      : aiFeatures.filter(f => f.status === selectedFilter);

  const stats = {
    totalFeatures: aiFeatures.length,
    activeFeatures: aiFeatures.filter(f => f.status === 'active').length,
    totalUsers: aiFeatures.reduce((sum, f) => sum + f.userCount, 0),
    averageSuccessRate:
      aiFeatures.reduce((sum, f) => sum + f.successRate, 0) / aiFeatures.length,
  };

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
      contentContainerStyle={styles.contentContainer}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
      }
    >
      {/* Info Header */}
      <View style={styles.header}>
        <View style={styles.titleRow}>
          <Brain size={32} color={theme.colors.primary} />
          <View style={styles.titleContainer}>
            <Text variant='headlineMedium' style={styles.title}>
              AI Experiments Hub
            </Text>
            <Text variant='bodyMedium' style={styles.subtitle}>
              Explore and test all AI-powered features and experiments
            </Text>
          </View>
        </View>
      </View>

      {/* Quick Stats */}
      <Surface style={styles.statsCard} elevation={1}>
        <View style={styles.statsRow}>
          <View style={styles.statItem}>
            <Zap size={20} color={theme.colors.primary} />
            <Text variant='labelSmall' style={styles.statLabel}>
              Features
            </Text>
            <Text variant='titleMedium' style={styles.statValue}>
              {stats.totalFeatures}
            </Text>
          </View>
          <View style={styles.statItem}>
            <Play size={20} color={theme.colors.secondary} />
            <Text variant='labelSmall' style={styles.statLabel}>
              Active
            </Text>
            <Text variant='titleMedium' style={styles.statValue}>
              {stats.activeFeatures}
            </Text>
          </View>
          <View style={styles.statItem}>
            <Users size={20} color={theme.colors.tertiary} />
            <Text variant='labelSmall' style={styles.statLabel}>
              Users
            </Text>
            <Text variant='titleMedium' style={styles.statValue}>
              {stats.totalUsers.toLocaleString()}
            </Text>
          </View>
          <View style={styles.statItem}>
            <Target size={20} color={theme.colors.primary} />
            <Text variant='labelSmall' style={styles.statLabel}>
              Success Rate
            </Text>
            <Text variant='titleMedium' style={styles.statValue}>
              {stats.averageSuccessRate.toFixed(1)}%
            </Text>
          </View>
        </View>
      </Surface>

      {/* Filter Chips */}
      <View style={styles.filterSection}>
        <Text variant='titleMedium' style={styles.filterTitle}>
          Filter by Status
        </Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          <View style={styles.filterChips}>
            {['all', 'active', 'beta', 'experimental', 'planned'].map(
              status => (
                <Chip
                  key={status}
                  mode={selectedFilter === status ? 'flat' : 'outlined'}
                  selected={selectedFilter === status}
                  onPress={() => setSelectedFilter(status)}
                  style={styles.filterChip}
                  textStyle={{
                    color:
                      selectedFilter === status
                        ? theme.colors.onPrimary
                        : theme.colors.onSurface,
                  }}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </Chip>
              )
            )}
          </View>
        </ScrollView>
      </View>

      {/* AI Experiments */}
      <View style={styles.featuresSection}>
        <Text variant='titleLarge' style={styles.sectionTitle}>
          All AI Experiments
        </Text>
        {filteredFeatures.map(feature => (
          <Card
            key={feature.id}
            style={styles.featureCard}
            onPress={() => handleFeaturePress(feature)}
          >
            <Card.Content>
              <View style={styles.featureHeader}>
                <View
                  style={[
                    styles.featureIcon,
                    { backgroundColor: theme.colors.surfaceVariant },
                  ]}
                >
                  {feature.icon}
                </View>
                <View style={styles.featureInfo}>
                  <Text variant='titleMedium' style={styles.featureTitle}>
                    {feature.title}
                  </Text>
                  <Text variant='bodySmall' style={styles.featureDescription}>
                    {feature.description}
                  </Text>
                </View>
                <View style={styles.featureStatus}>
                  <Chip
                    mode='outlined'
                    textStyle={{ color: getStatusColor(feature.status) }}
                    style={{ borderColor: getStatusColor(feature.status) }}
                    compact
                  >
                    {feature.status}
                  </Chip>
                </View>
              </View>

              <View style={styles.featureMetrics}>
                <View style={styles.metricRow}>
                  <View style={styles.metricItem}>
                    <Text variant='labelSmall' style={styles.metricLabel}>
                      Complexity
                    </Text>
                    <Chip
                      mode='outlined'
                      textStyle={{
                        color: getComplexityColor(feature.complexity),
                      }}
                      style={{
                        borderColor: getComplexityColor(feature.complexity),
                      }}
                      compact
                    >
                      {feature.complexity}
                    </Chip>
                  </View>
                  <View style={styles.metricItem}>
                    <Text variant='labelSmall' style={styles.metricLabel}>
                      Users
                    </Text>
                    <Text variant='bodyMedium' style={styles.metricValue}>
                      {feature.userCount.toLocaleString()}
                    </Text>
                  </View>
                  <View style={styles.metricItem}>
                    <Text variant='labelSmall' style={styles.metricLabel}>
                      Success Rate
                    </Text>
                    <Text variant='bodyMedium' style={styles.metricValue}>
                      {feature.successRate}%
                    </Text>
                  </View>
                </View>
              </View>

              <View style={styles.featureActions}>
                <Button
                  mode='contained'
                  onPress={() => handleFeaturePress(feature)}
                  style={styles.testButton}
                  icon='play'
                >
                  Test Feature
                </Button>
                <Button
                  mode='outlined'
                  onPress={() => {}} // TODO: Show detailed analytics
                  style={styles.analyticsButton}
                  icon='chart-line'
                >
                  Analytics
                </Button>
              </View>
            </Card.Content>
          </Card>
        ))}
      </View>

      {/* Quick Actions */}
      <View style={styles.quickActionsSection}>
        <Text variant='titleLarge' style={styles.sectionTitle}>
          Popular Experiments
        </Text>
        <View style={styles.quickActionsGrid}>
          <Button
            mode='contained'
            onPress={() => (navigation as any).navigate('aiOnboarding')}
            style={styles.quickActionButton}
            icon='account-plus'
            contentStyle={styles.quickActionContent}
          >
            Smart Onboarding
          </Button>
          <Button
            mode='contained'
            onPress={() => (navigation as any).navigate('intelligentSearch')}
            style={styles.quickActionButton}
            icon='magnify'
            contentStyle={styles.quickActionContent}
          >
            Intelligent Search
          </Button>
          <Button
            mode='contained'
            onPress={() => (navigation as any).navigate('aiAssessment')}
            style={styles.quickActionButton}
            icon='clipboard-check'
            contentStyle={styles.quickActionContent}
          >
            Skill Assessment
          </Button>
          <Button
            mode='contained'
            onPress={() =>
              (navigation as any).navigate('premiumAnalyticsDashboard')
            }
            style={styles.quickActionButton}
            icon='chart-line'
            contentStyle={styles.quickActionContent}
          >
            Premium Analytics
          </Button>
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
    paddingBottom: 32,
  },
  header: {
    marginBottom: 24,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  titleContainer: {
    marginLeft: 16,
    flex: 1,
  },
  title: {
    fontWeight: 'bold',
    marginBottom: 4,
  },
  subtitle: {
    opacity: 0.7,
  },
  statsCard: {
    padding: 16,
    marginBottom: 24,
    borderRadius: 12,
  },
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  statItem: {
    alignItems: 'center',
    flex: 1,
  },
  statLabel: {
    marginTop: 4,
    opacity: 0.7,
  },
  statValue: {
    marginTop: 2,
    fontWeight: 'bold',
  },
  filterSection: {
    marginBottom: 24,
  },
  filterTitle: {
    marginBottom: 12,
    fontWeight: 'bold',
  },
  filterChips: {
    flexDirection: 'row',
    gap: 8,
  },
  filterChip: {
    marginRight: 8,
  },
  featuresSection: {
    marginBottom: 24,
  },
  sectionTitle: {
    marginBottom: 16,
    fontWeight: 'bold',
  },
  featureCard: {
    marginBottom: 16,
    borderRadius: 12,
  },
  featureHeader: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  featureIcon: {
    marginRight: 16,
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    borderRadius: 24,
  },
  featureInfo: {
    flex: 1,
    marginRight: 12,
  },
  featureTitle: {
    fontWeight: 'bold',
    marginBottom: 4,
  },
  featureDescription: {
    opacity: 0.7,
    lineHeight: 18,
  },
  featureStatus: {
    alignItems: 'flex-start',
  },
  featureMetrics: {
    marginBottom: 16,
  },
  metricRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  metricItem: {
    alignItems: 'center',
    flex: 1,
  },
  metricLabel: {
    marginBottom: 4,
    opacity: 0.7,
  },
  metricValue: {
    fontWeight: 'bold',
  },
  featureActions: {
    flexDirection: 'row',
    gap: 12,
  },
  testButton: {
    flex: 1,
  },
  analyticsButton: {
    flex: 1,
  },
  quickActionsSection: {
    marginBottom: 24,
  },
  quickActionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  quickActionButton: {
    flex: 1,
    minWidth: '45%',
  },
  quickActionContent: {
    height: 48,
  },
});
